import { loadSessionField } from '../../auth/sessionProfileStore'
import React, { useId, useMemo, useState } from 'react'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Divider from '@mui/material/Divider'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import { CaretDown } from '@phosphor-icons/react'
import cookie from 'react-cookies'
import { useNavigate } from 'react-router-dom'
import { signOut } from '../../auth/authClient'
import { useAuth } from '../../auth/AuthContext'
import TowerProfileMenuPanel from './TowerProfileMenuPanel'
import {
  UI_COLOR_BORDER_LIGHT,
  UI_COLOR_PRIMARY,
  UI_COLOR_TEXT_HEADING,
  UI_COLOR_TEXT_MUTED_LIGHT,
  UI_COLOR_TEXT_NEUTRAL,
  UI_COLOR_WHITE
} from '../../constants/uiColors'

function getInitials(nameOrEmail) {
  const value = String(nameOrEmail || '').trim()
  if (!value) return 'U'
  if (value.includes('@')) {
    return value.slice(0, 2).toUpperCase()
  }
  const parts = value.split(/\s+/).filter(Boolean)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
}

const UserSessionMenu = ({ compact = false, towerTypography = false }) => {
  const { isBootstrapping } = useAuth()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const menuId = useId()

  const { displayName, email, businessName, initials, workspaceTag } = useMemo(() => {
    const userName = cookie.load('user_name')
    const userEmail = cookie.load('user_email')
    const business = loadSessionField('business')
    const name = userName || userEmail || 'Usuario'
    const businessLabel = business || ''
    return {
      displayName: name,
      email: userEmail || '',
      businessName: businessLabel,
      initials: getInitials(userName || userEmail),
      workspaceTag: businessLabel
    }
  }, [isBootstrapping])

  const handleClose = () => setAnchorEl(null)

  const handleOpen = (event) => setAnchorEl(event.currentTarget)

  const trigger = towerTypography ? (
    <button
      type="button"
      className="profile-btn"
      aria-label={`Abrir menú de ${displayName}`}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-controls={open ? menuId : undefined}
      onClick={handleOpen}
    >
      <span className="profile-avatar">{initials}</span>
      {!compact ? (
        <>
          <span className="profile-copy">
            <b>{displayName}</b>
            {businessName ? <small>{businessName}</small> : null}
          </span>
          <CaretDown className="icon" size={14} weight="bold" aria-hidden="true" />
        </>
      ) : null}
    </button>
  ) : (
    <ButtonBase
      aria-label="Menú de usuario"
      aria-haspopup="menu"
      aria-expanded={open}
      aria-controls={open ? menuId : undefined}
      onClick={handleOpen}
      className="top-bar-user-btn"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: compact ? 0.5 : 1,
        py: 0.5,
        borderRadius: '999px',
        '&:hover': { bgcolor: alpha(UI_COLOR_PRIMARY, 0.08) }
      }}
    >
      <Avatar
        sx={{
          width: 34,
          height: 34,
          fontSize: 13,
          fontWeight: 700,
          bgcolor: UI_COLOR_PRIMARY,
          color: UI_COLOR_WHITE
        }}
      >
        {initials}
      </Avatar>
      {!compact && (
        <>
          <Box
            className="top-bar-user-meta"
            sx={{
              textAlign: 'left',
              minWidth: 0,
              maxWidth: 140,
              display: { xs: 'none', md: 'block' }
            }}
          >
            <Typography
              noWrap
              sx={{
                fontWeight: 700,
                fontSize: 13.5,
                color: UI_COLOR_TEXT_HEADING,
                lineHeight: 1.2
              }}
            >
              {displayName}
            </Typography>
            {businessName ? (
              <Typography
                noWrap
                sx={{
                  fontSize: 11,
                  color: UI_COLOR_TEXT_MUTED_LIGHT,
                  lineHeight: 1.2
                }}
              >
                {businessName}
              </Typography>
            ) : null}
          </Box>
          <KeyboardArrowDownRoundedIcon
            className="top-bar-user-chevron"
            sx={{
              fontSize: 20,
              color: UI_COLOR_TEXT_NEUTRAL,
              display: { xs: 'none', md: 'block' }
            }}
          />
        </>
      )}
    </ButtonBase>
  )

  return (
    <>
      {trigger}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{
          id: menuId,
          disablePadding: true,
          sx: { p: 0, overflow: 'hidden' }
        }}
        slotProps={{
          paper: {
            className: towerTypography ? 'top-bar-tower-menu top-bar-tower-profile' : undefined,
            sx: towerTypography
              ? {
                  mt: 1,
                  width: 262,
                  borderRadius: '15px',
                  border: `1px solid ${UI_COLOR_BORDER_LIGHT}`,
                  boxShadow: `0 16px 40px ${alpha('#0F172A', 0.12)}`,
                  overflow: 'hidden'
                }
              : {
                  mt: 1,
                  minWidth: 262,
                  borderRadius: '15px',
                  border: `1px solid ${UI_COLOR_BORDER_LIGHT}`,
                  boxShadow: `0 16px 40px ${alpha('#0F172A', 0.12)}`
                }
          }
        }}
      >
        {towerTypography ? (
          <TowerProfileMenuPanel
            displayName={displayName}
            email={email}
            workspaceTag={workspaceTag}
            initials={initials}
            onMyProfile={() => {
              handleClose()
              navigate('/perfil')
            }}
            onChangePassword={() => {
              handleClose()
              navigate('/perfil/seguridad')
            }}
            onSignOut={() => {
              handleClose()
              signOut().finally(() => {
                window.location.replace('/logout')
              })
            }}
          />
        ) : (
          [
            <Box key="profile-header" sx={{ px: 2, py: 1.5 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 14,
                  color: UI_COLOR_TEXT_HEADING
                }}
              >
                {displayName}
              </Typography>
              {email ? (
                <Typography
                  sx={{
                    fontSize: 12.5,
                    fontWeight: 400,
                    color: UI_COLOR_TEXT_NEUTRAL,
                    mt: 0.25
                  }}
                >
                  {email}
                </Typography>
              ) : null}
              {businessName ? (
                <span
                  style={{
                    display: 'inline-flex',
                    marginTop: 12,
                    padding: '4px 7px',
                    borderRadius: 6,
                    background: '#f3f7fc',
                    color: '#7f98b5',
                    fontSize: 10
                  }}
                >
                  {businessName}
                </span>
              ) : null}
            </Box>,
            <Divider key="profile-divider" />,
            <MenuItem
              key="profile-my-profile"
              onClick={() => {
                handleClose()
                navigate('/perfil')
              }}
              sx={{ fontSize: 13, fontWeight: 600, color: UI_COLOR_TEXT_HEADING }}
            >
              Mi perfil
            </MenuItem>,
            <MenuItem
              key="profile-change-password"
              onClick={() => {
                handleClose()
                navigate('/perfil/seguridad')
              }}
              sx={{ fontSize: 13, fontWeight: 600, color: UI_COLOR_TEXT_HEADING }}
            >
              Cambiar contraseña
            </MenuItem>,
            <MenuItem
              key="profile-sign-out"
              onClick={() => {
                handleClose()
                signOut().finally(() => {
                  window.location.replace('/logout')
                })
              }}
              sx={{ fontSize: 13, fontWeight: 600, color: UI_COLOR_TEXT_HEADING }}
            >
              Cerrar sesión
            </MenuItem>
          ]
        )}
      </Menu>
    </>
  )
}

export default React.memo(UserSessionMenu)
